import { message } from 'antd';
import { formatSeasonAnchorDate, getSeasonContext } from '@data-management/shared';
import { formatFullWidthPunctuation } from './textFormatter';

export interface PdfComposerOptions {
  tarotImageUrl: string;
  llmResult: string;
  title?: string;
  templateId?: string;
}

interface PdfSection {
  title: string;
  content: string;
}

interface PageImage {
  data: Uint8Array;
  width: number;
  height: number;
}

const A4_WIDTH_PX = 1240; // A4宽度（150DPI）
const A4_HEIGHT_PX = 1754; // A4高度（150DPI）
const PAGE_WIDTH_PT = 595.28; // A4宽度（PDF点）
const PAGE_HEIGHT_PT = 841.89; // A4高度（PDF点）
const HORIZONTAL_MARGIN = 150;
const TOP_MARGIN = 180;
const BOTTOM_MARGIN = 180;
const FONT_FAMILY = '"SimSun", "Songti SC", "Noto Serif SC", "Microsoft YaHei", "PingFang SC", serif';
const TITLE_FONT_SIZE = 48;
const TITLE_LINE_HEIGHT = Math.round(TITLE_FONT_SIZE * 1.35);
const BODY_FONT_SIZE = 28;
const BODY_LINE_HEIGHT = Math.round(BODY_FONT_SIZE * 1.6);
const TIP_TEXT = '温馨提示\n关于这次解析我会实话实说地分享看到的情况，包括需要注意的地方，目的是帮助你更好地改善。\n但要记住，本次解析是基于你现在的状态和想法，不要把任何指引当成绝对的宿命，你随时可以通过调整心态和行动来改写结果。';
const TIP_FONT_SIZE = 20;
const TIP_LINE_HEIGHT = Math.round(TIP_FONT_SIZE * 1.6);
const TIP_TEXT_COLOR = '#888888';
const TITLE_BODY_SPACING = 20;
const SECTION_SPACING = 40;

export class PdfComposer {
  static async composePdf(options: PdfComposerOptions): Promise<Blob> {
    const { tarotImageUrl, llmResult, title = '', templateId = '' } = options;

    try {
      const tarotImage = await this.loadImage(tarotImageUrl);
      const formattedLlmResult = formatFullWidthPunctuation(llmResult);
      const sections = this.parseSections(formattedLlmResult, templateId);

      const pages: PageImage[] = [];

      // 首页面：牌阵图
      const firstPageCanvas = this.createBlankPage();
      this.drawFirstPage(firstPageCanvas, tarotImage, title);
      pages.push(await this.canvasToJpeg(firstPageCanvas));

      // 文本页面
      const textPages = this.renderTextSections(sections);
      for (const pageCanvas of textPages) {
        pages.push(await this.canvasToJpeg(pageCanvas));
      }

      return this.buildPdfFromImages(pages);
    } catch (error) {
      console.error('PDF生成失败:', error);
      message.error('PDF生成失败，请稍后重试');
      throw error;
    }
  }

  static createPreviewUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  static downloadPdf(blob: Blob, fileName?: string): void {
    const url = this.createPreviewUrl(blob);
    const link = document.createElement('a');
    link.href = url;
    const seasonContext = getSeasonContext();
    const anchorDate = formatSeasonAnchorDate(seasonContext.anchorDate);
    link.download = fileName && fileName.trim() ? fileName : `${anchorDate} ${seasonContext.term}四季牌.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }

  private static async loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('牌阵图片加载失败'));
      img.src = url;
    });
  }

  private static parseSections(llmResult: string, _templateId?: string): PdfSection[] {
    if (!llmResult || !llmResult.trim()) {
      return [];
    }

    const normalized = llmResult
      .replace(/\r\n/g, '\n')
      .replace(/\u200b/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/\*\*/g, '')
      .trim();

    if (!normalized) {
      return [];
    }

    const sections: PdfSection[] = [];
    const titlePattern = /##([^#]+)##/g;
    const matches = [...normalized.matchAll(titlePattern)];

    if (matches.length === 0) {
      sections.push({
        title: '',
        content: normalized
      });
      return sections;
    }

    let lastIndex = 0;
    let currentTitle: string | null = null;

    matches.forEach(match => {
      const [fullMatch, rawTitle] = match;
      const matchIndex = match.index ?? 0;

      const preceding = normalized.slice(lastIndex, matchIndex).trim();

      if (currentTitle !== null || preceding) {
        sections.push({
          title: currentTitle ?? '',
          content: preceding
        });
      }

      currentTitle = rawTitle.trim();
      lastIndex = matchIndex + fullMatch.length;
    });

    const remaining = normalized.slice(lastIndex).trim();

    if (currentTitle !== null || remaining) {
      sections.push({
        title: (currentTitle ?? '').trim(),
        content: remaining
      });
    }

    return sections.filter(section => section.title || section.content);
  }

  private static createBlankPage(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = A4_WIDTH_PX;
    canvas.height = A4_HEIGHT_PX;
    const ctx = this.getCanvasContext(canvas);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return canvas;
  }

  private static drawFirstPage(canvas: HTMLCanvasElement, tarotImage: HTMLImageElement, _title: string): void {
    const ctx = this.getCanvasContext(canvas);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let currentY = TOP_MARGIN;
    const contentWidth = canvas.width - (HORIZONTAL_MARGIN * 2);

    const availableHeight = canvas.height - currentY - BOTTOM_MARGIN;
    const imageScale = Math.min(contentWidth / tarotImage.width, availableHeight / tarotImage.height);
    const drawWidth = tarotImage.width * imageScale;
    const drawHeight = tarotImage.height * imageScale;
    const drawX = HORIZONTAL_MARGIN + ((contentWidth - drawWidth) / 2);
    const drawY = currentY + ((availableHeight - drawHeight) / 2);

    ctx.drawImage(tarotImage, drawX, drawY, drawWidth, drawHeight);
  }

  private static renderTextSections(sections: PdfSection[]): HTMLCanvasElement[] {
    const canvases: HTMLCanvasElement[] = [];
    let canvas = this.createBlankPage();
    let ctx = this.getCanvasContext(canvas);

    let currentY = TOP_MARGIN;
    let pageHasContent = false;
    const contentWidth = canvas.width - (HORIZONTAL_MARGIN * 2);

    const startNewPage = () => {
      if (pageHasContent) {
        canvases.push(canvas);
      }
      canvas = this.createBlankPage();
      ctx = this.getCanvasContext(canvas);
      currentY = TOP_MARGIN;
      pageHasContent = false;
    };

    const ensureSpace = (heightNeeded: number) => {
      if (currentY + heightNeeded > canvas.height - BOTTOM_MARGIN) {
        startNewPage();
      }
    };

    sections.forEach(section => {
      const titleFont = `bold ${TITLE_FONT_SIZE}px ${FONT_FAMILY}`;
      const bodyFont = `${BODY_FONT_SIZE}px ${FONT_FAMILY}`;

      const trimmedTitle = section.title.trim();
      const hasTitle = trimmedTitle.length > 0;
      const hasBody = section.content && section.content.trim().length > 0;

      const titleLines = hasTitle ? this.wrapText(ctx, trimmedTitle, contentWidth, titleFont) : [];
      const bodyLines = hasBody ? this.wrapParagraphs(ctx, section.content, contentWidth, bodyFont) : [];

      if (hasTitle) {
        titleLines.forEach(line => {
          ensureSpace(TITLE_LINE_HEIGHT);
          ctx.font = titleFont;
          ctx.fillStyle = '#1a1a1a';
          if (line) {
            ctx.fillText(line, HORIZONTAL_MARGIN, currentY);
            pageHasContent = true;
          }
          currentY += TITLE_LINE_HEIGHT;
        });
      }

      if (hasTitle && hasBody) {
        currentY += TITLE_BODY_SPACING;
      }

      if (hasBody) {
        bodyLines.forEach(line => {
          ensureSpace(BODY_LINE_HEIGHT);
          ctx.font = bodyFont;
          ctx.fillStyle = '#2c3e50';
          if (line) {
            ctx.fillText(line, HORIZONTAL_MARGIN, currentY);
            pageHasContent = true;
          }
          currentY += BODY_LINE_HEIGHT;
        });
      }

      if (hasTitle || hasBody) {
        currentY += SECTION_SPACING;
      }
    });

    const tipFont = `${TIP_FONT_SIZE}px ${FONT_FAMILY}`;
    const tipLines = this.wrapTextWithLineBreaks(ctx, TIP_TEXT, contentWidth, tipFont);
    tipLines.forEach(line => {
      ensureSpace(TIP_LINE_HEIGHT);
      ctx.font = tipFont;
      ctx.fillStyle = TIP_TEXT_COLOR;
      if (line) {
        ctx.fillText(line, HORIZONTAL_MARGIN, currentY);
        pageHasContent = true;
      }
      currentY += TIP_LINE_HEIGHT;
    });

    if (pageHasContent) {
      canvases.push(canvas);
    }

    return canvases;
  }

  private static wrapParagraphs(ctx: CanvasRenderingContext2D, content: string, maxWidth: number, font: string): string[] {
    if (!content) {
      return [''];
    }

    const paragraphs = content.split(/\n+/).map(paragraph => paragraph.trim());

    const lines = paragraphs
      .map(paragraph => paragraph ? this.wrapText(ctx, paragraph, maxWidth, font) : [''])
      .reduce<string[]>((acc, wrappedLines, idx, arr) => {
        acc.push(...wrappedLines);
        if (idx < arr.length - 1) {
          acc.push('');
        }
        return acc;
      }, []);

    return lines.length > 0 ? lines : [''];
  }

  private static wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, font: string): string[] {
    ctx.font = font;
    const characters = Array.from(text);
    const lines: string[] = [];
    let currentLine = '';

    characters.forEach(char => {
      const tentative = currentLine + char;
      const width = ctx.measureText(tentative).width;
      if (width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = char;
      } else {
        currentLine = tentative;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    if (lines.length === 0) {
      return [''];
    }

    return lines;
  }

  private static wrapTextWithLineBreaks(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, font: string): string[] {
    if (!text) {
      return [''];
    }

    const lines: string[] = [];
    const rawLines = text.split('\n');
    rawLines.forEach(rawLine => {
      if (rawLine.trim() === '') {
        lines.push('');
        return;
      }
      lines.push(...this.wrapText(ctx, rawLine, maxWidth, font));
    });

    return lines.length > 0 ? lines : [''];
  }

  private static async canvasToJpeg(canvas: HTMLCanvasElement): Promise<PageImage> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Canvas转JPEG失败'));
          return;
        }
        blob.arrayBuffer()
          .then(buffer => resolve({
            data: new Uint8Array(buffer),
            width: canvas.width,
            height: canvas.height
          }))
          .catch(reject);
      }, 'image/jpeg', 0.92);
    });
  }

  private static buildPdfFromImages(pages: PageImage[]): Blob {
    if (pages.length === 0) {
      throw new Error('没有可用于生成PDF的页面');
    }

    const encoder = new TextEncoder();
    const objects = new Map<number, (string | Uint8Array)[]>();
    let nextObjectId = 1;

    const catalogObjectId = nextObjectId++;
    const pagesObjectId = nextObjectId++;

    const pageRefs: number[] = [];

    pages.forEach((page, index) => {
      const imageName = `/Im${index + 1}`;

      const imageObjectId = nextObjectId++;
      objects.set(imageObjectId, [
        `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
        page.data,
        `\nendstream`
      ]);

      const contentStream = `q ${PAGE_WIDTH_PT.toFixed(2)} 0 0 ${PAGE_HEIGHT_PT.toFixed(2)} 0 0 cm ${imageName} Do Q`;
      const contentBuffer = encoder.encode(contentStream);
      const contentObjectId = nextObjectId++;
      objects.set(contentObjectId, [
        `<< /Length ${contentBuffer.length} >>\nstream\n${contentStream}\nendstream`
      ]);

      const pageObjectId = nextObjectId++;
      objects.set(pageObjectId, [
        `<< /Type /Page /Parent ${pagesObjectId} 0 R /MediaBox [0 0 ${PAGE_WIDTH_PT.toFixed(2)} ${PAGE_HEIGHT_PT.toFixed(2)}] /Resources << /XObject << ${imageName} ${imageObjectId} 0 R >> /ProcSet [/PDF /ImageC] >> /Contents ${contentObjectId} 0 R >>`
      ]);

      pageRefs.push(pageObjectId);
    });

    objects.set(pagesObjectId, [
      `<< /Type /Pages /Count ${pageRefs.length} /Kids [${pageRefs.map(ref => `${ref} 0 R`).join(' ')}] >>`
    ]);

    objects.set(catalogObjectId, [
      `<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`
    ]);

    const totalObjects = nextObjectId - 1;
    const offsets: number[] = new Array(totalObjects + 1).fill(0);
    const chunks: Uint8Array[] = [];
    let offset = 0;

    const append = (chunk: string | Uint8Array) => {
      let data: Uint8Array;
      if (typeof chunk === 'string') {
        data = encoder.encode(chunk);
      } else {
        data = chunk;
      }
      chunks.push(data);
      offset += data.length;
    };

    append('%PDF-1.4\n');

    for (let objectId = 1; objectId <= totalObjects; objectId++) {
      const objectContent = objects.get(objectId);
      if (!objectContent) {
        throw new Error(`缺少PDF对象: ${objectId}`);
      }
      offsets[objectId] = offset;
      append(`${objectId} 0 obj\n`);
      objectContent.forEach(part => append(part));
      append('\nendobj\n');
    }

    const xrefOffset = offset;
    append(`xref\n0 ${totalObjects + 1}\n`);
    append('0000000000 65535 f \n');
    for (let objectId = 1; objectId <= totalObjects; objectId++) {
      const position = offsets[objectId].toString().padStart(10, '0');
      append(`${position} 00000 n \n`);
    }

    append(`trailer << /Size ${totalObjects + 1} /Root ${catalogObjectId} 0 R >>\n`);
    append(`startxref\n${xrefOffset}\n%%EOF`);

    return new Blob(chunks, { type: 'application/pdf' });
  }

  private static getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('无法获取Canvas上下文');
    }
    return ctx;
  }
}
